import { Add, Delete, Edit, Mail, Phone, User } from "@payping/icons";
import { getCustomers, removeCustomer } from "@payping/redux/actions";
import { ensureString } from "@payping/utilities-shared/string";
import {
  Button,
  Col,
  Copy,
  Empty,
  Flex,
  LegacyCard,
  LegacyCardList,
  Modal,
  Row,
  Space,
  TableAction,
  Typography,
  useMessagedAsyncRetry,
} from "@payping/web-components";
import type { TablePaginationConfig } from "@payping/web-components/components/table/types";
import { FC, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { DisplayModeEnum } from "@payping/web-components/legacyComponents/List/types";
import type { ListCardProps } from "@payping/web-components/legacyComponents/List/types";

import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import { CustomerFilter } from "@/components/DashCustomer/CustomerListFilter";
import { CustomerSubNavCreateBulkTestId, CustomerSubNavCreateTestId } from "@/assets/testIds";
import HandlingCrispInMobile from "@/components/mobile/HandlingCrispInMobile";
import { DashboardRoutes } from "@/router";
import { DropdownButton } from "@/core/component";
import { ListBulkActions } from "./listBulkActions";
import { useQueryStates } from "nuqs";
import { enumNumbers, searchParamsParser } from "@/core/utils/search-params-parser";
import { CustomerTypeEnum } from "@/core/consts";

export interface CustomerListFilters {
  customerType?: CustomerTypeEnum;
  searchText?: string;
}

const List: FC = () => {
  const dispatch = useAppDispatch();
  const [displayMode, setDisplayMode] = useState<DisplayModeEnum>(DisplayModeEnum.Card);
  const location = useLocation();
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const ROUTES = DashboardRoutes.myShop.customer;

  const [searchParams, setSearchParams] = useQueryStates({
    customerType: searchParamsParser.parseAsIntegerEnum<CustomerTypeEnum>(enumNumbers(CustomerTypeEnum)),

    pageNumber: searchParamsParser.parseAsInteger,
    pageSize: searchParamsParser.parseAsInteger,
    searchText: searchParamsParser.parseAsString,
  });

  const pageNumber = searchParams.pageNumber ?? 1;
  const pageSize = searchParams.pageSize ?? 12;

  const customersList = useAppSelector((state) => state.customers.list);
  const customersCount = useAppSelector((state) => state.customers.totalCount);
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const [modal, contextHolder] = Modal.useModal();

  const { loading, retry } = useMessagedAsyncRetry(
    async () => {
      await dispatch(
        getCustomers({
          customerType: searchParams.customerType ?? CustomerTypeEnum.All,
          limit: pageSize,
          offset: (pageNumber - 1) * pageSize,
          searchText: searchParams.searchText ?? undefined,
        }),
      );
    },
    [dispatch, searchParams, pageSize, pageNumber],
    { success: false, useProgress: true },
  );

  async function handleDelete(code?: string): Promise<void> {
    modal.confirm({
      cancelText: t("cancel"),
      content: t("customer.remove.confirm"),
      mask: { closable: true },
      okText: t("ok"),
      onOk: async () => {
        await dispatch(removeCustomer(code as string));
        await retry();
      },
    });
  }

  const data: ListCardProps["dataSource"] = customersList.map((customer, id) => {
    const name =
      customer.firstName && customer.lastName
        ? `${customer.firstName} ${customer.lastName}`
        : customer.firstName || customer.lastName;
    return {
      actions: [
        <Space key={id} size={"middle"}>
          <Link
            to={{
              pathname: ROUTES.edit.url.replace(":code", customer.code || ""),
            }}
            state={{ locationSearch: location.search }}
            key={0}
          >
            <TableAction Icon={Edit} iconColor={"cta"} label={t("edit")} />
          </Link>
          <a key={1}>
            <TableAction
              disabled={selectedRowKeys.length > 1}
              Icon={Delete}
              iconColor={"danger"}
              label={t("delete")}
              onClick={async (event) => {
                event.stopPropagation();
                await handleDelete(customer.code);
              }}
              key={1}
            />
          </a>
        </Space>,
      ],
      image: { type: "avatar", content: customer.userPhoto || <User width={"12px"} className={"color--white"} /> },
      descriptions: [
        <Flex key={id} vertical gap={16}>
          {/* one */}
          <Flex justify={"space-between"} align={"center"}>
            <Flex justify={"space-between"} align={"center"}>
              <Mail width={16} strokeWidth={"2px"} className="tw:ml-[3px]!" />
              {t("email")}
            </Flex>
            {ensureString({ text: customer.email ?? "", fallbackValue: "-" })}
          </Flex>
          {/* two */}
          <Flex justify={"space-between"} align={"center"}>
            <Flex justify={"space-between"}>
              <Phone width={16} className="tw:ml-[3px]!" />
              {t("mobileNumber")}
            </Flex>
            {ensureString({ text: customer.phone ?? "", fallbackValue: "-" })}
          </Flex>
        </Flex>,
      ],
      key: String(customer.code),
      title: {
        primary: name || ensureString({ text: customer.businessName ?? "", fallbackValue: "-" }),
        secondary: <Copy text={customer.code || ""}>{customer.code}</Copy>,
      },
    };
  });

  const pagination: TablePaginationConfig = {
    "data-test": "customer-list-pagination",
    onChange: (page, newSize) => {
      setSelectedRowKeys([]);
      setSearchParams((pre) => ({ ...pre, pageNumber: page, pageSize: newSize }));
    },
    onShowSizeChange: (_currentPage, newSize) => {
      setSelectedRowKeys([]);
      setSearchParams((pre) => ({ ...pre, pageNumber: 1, pageSize: newSize }));
    },
    pageNumber,
    pageSize,
    total: customersCount,
  };

  return (
    <HandlingCrispInMobile>
      {loading || data?.length > 0 || location.search ? (
        <Space orientation={"vertical"} size={"middle"} className={"tw:w-full"}>
          <CustomerFilter
            setFilters={(filters) => {
              setSearchParams((pre) => ({ ...pre, ...filters }));
            }}
            filters={{
              customerType: searchParams.customerType ?? CustomerTypeEnum.All,
              searchText: searchParams.searchText ?? undefined,
            }}
            displayMode={displayMode}
            setDisplayMode={setDisplayMode}
            loading={loading}
            retry={retry}
            setSelectedRowKeys={setSelectedRowKeys}
          />
          <LegacyCardList
            className={"tw:w-full"}
            loading={loading}
            pagination={pagination}
            grid={{ xs: 1, sm: 2, md: 2, lg: 3, xl: 3, xxl: 4, xxxl: 4 }}
            itemSelection={{
              onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys.map((r) => String(r))),
              selectedItemKeys: selectedRowKeys,
            }}
            dataSource={data}
            mode={displayMode}
          />
        </Space>
      ) : (
        <Empty
          description={
            <Space orientation={"vertical"} size={"small"} className={"tw:w-full"}>
              <Typography.Text strong>{t("customer.emptyList.title")}</Typography.Text>
              <Typography.Text>{t("customer.emptyList.content")}</Typography.Text>
              <DropdownButton
                className={"tw:flex tw:justify-center"}
                menu={{
                  items: [
                    {
                      label: t("customer.createBulk.createGroupCustomerViaExcel"),
                      key: "1",
                      onClick: (): void => {
                        navigate(
                          {
                            pathname: ROUTES.createBulk.url,
                          },
                          {
                            state: { locationSearch: location.search },
                          },
                        );
                      },

                      "data-test": CustomerSubNavCreateBulkTestId,
                    },
                  ],
                }}
                key={0}
                type={"primary"}
                buttonIcon={<Add width={14} height={14} />}
                onClick={(): void => {
                  navigate(
                    {
                      pathname: ROUTES.create.url,
                    },
                    {
                      state: { locationSearch: location.search },
                    },
                  );
                }}
                data-test={CustomerSubNavCreateTestId}
              >
                {` ${t("customer.add.button")}`}
              </DropdownButton>
            </Space>
          }
        >
          <Row gutter={[8, 8]} className={"tw:w-full"}>
            <Col xs={24} md={12}>
              <LegacyCard.Ant className="tw:text-start">
                <Space orientation={"vertical"} size={"small"} className={"tw:w-full"}>
                  <Typography.Text type={"secondary"} className={"tw:text-xs"}>
                    {t("customer.emptyList.cards.customer.upperTitle")}
                  </Typography.Text>
                  <Typography.Text strong> {t("customer.emptyList.cards.customer.title")}</Typography.Text>
                  <Typography.Text type={"secondary"}>{t("customer.emptyList.cards.customer.content")}</Typography.Text>
                  <Space>
                    <Link to={DashboardRoutes.connections.developers.root.url}>
                      <Button type={"link"} className={"tw:p-0!"}>
                        {t("common.receiveToken")}
                      </Button>
                    </Link>

                    <Link to={DashboardRoutes.connections.developers.root.url}>
                      <Button type={"link"} className={"tw:p-0!"}>
                        {t("common.guideToTechnicalDocumentation")}
                      </Button>
                    </Link>
                  </Space>
                </Space>
              </LegacyCard.Ant>
            </Col>
            <Col xs={24} md={12}>
              <LegacyCard.Ant className="tw:text-start">
                <Space orientation={"vertical"} size={"small"} className={"tw:w-full"}>
                  <Typography.Text type={"secondary"} className={"tw:text-xs"}>
                    {t("customer.emptyList.cards.plugins.upperTitle")}
                  </Typography.Text>
                  <Typography.Text strong> {t("customer.emptyList.cards.plugins.title")}</Typography.Text>
                  <Typography.Text type={"secondary"}>{t("customer.emptyList.cards.plugins.content")} </Typography.Text>
                  <Link to={DashboardRoutes.connections.developers.root.url}>
                    <Button type={"link"} className={"tw:p-0!"}>
                      {t("common.readMore")}
                    </Button>
                  </Link>
                </Space>
              </LegacyCard.Ant>
            </Col>
          </Row>
        </Empty>
      )}

      <ListBulkActions
        retry={retry}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        data={data}
      />
      {contextHolder}
    </HandlingCrispInMobile>
  );
};

export default List;
